import { HeaderIcons } from '../shared/icons';
import React, { useState, useEffect, useRef } from 'react';
import { WebSocketAdapter } from '../../utils/WebSocketAdapter';
import Header from '../shared/Header';
import { StandingsSidebar, LoadingQuestion, TimerSection, WaitingSpinner } from '../shared';
import { GameEndScreen } from './GameEndScreen';
import { calculateTimeRemaining } from '../../utils/gameUtils';
import { useGameEndScreenData } from '../../hooks/useGameEndScreenData';
import { gameUsesTeams } from '../../utils/teamGames';
import type { GameStatistics } from '../../types/game';
import { HouseRunTaskCard } from './houseRun';
import type { HouseRunTask } from './houseRun/HouseRunTaskCard';
import '../../App.css';
import '../../styles/game-layout.css';
import '../../styles/game-screen.css';
import { useTranslation } from '../../i18n';

interface TeamSubmission {
  teamId: 'A' | 'B';
  playerName: string;
  photoData: string;
  task?: HouseRunTask;
  resolved?: boolean;
  accepted?: boolean;
}

interface HostQuestion {
  questionId?: string;
  id?: string;
  question: string;
  title?: string;
  timeLimit: number;
  questionNumber: number;
  totalQuestions: number;
  startTime: number;
  isHouseRun?: boolean;
  interesting?: string;
  taskA?: HouseRunTask;
  taskB?: HouseRunTask;
}

interface HouseRunHostDisplayProps {
  roomState: {
    roomName: string;
    gameType: string;
    hostId: string;
    players: Array<{ id: string; name: string; score: number; avatar?: string; teamId?: 'A' | 'B' }>;
    teams?: { A: { id: 'A'; name: string; score: number }; B: { id: 'B'; name: string; score: number } };
    gameState: 'waiting' | 'playing' | 'finished';
    currentQuestion?: HostQuestion;
    questionStartTime?: number;
  };
  socket: WebSocketAdapter;
  onBackToGameSelection?: () => void;
  myPlayerId?: string;
  hostIsPlaying?: boolean;
  onGoHome?: () => void;
  onLoadingTimeout?: () => void;
}

const HouseRunHostDisplay: React.FC<HouseRunHostDisplayProps> = ({
  roomState,
  socket,
  onBackToGameSelection,
  hostIsPlaying = false,
  onGoHome,
  onLoadingTimeout,
}) => {
  const { t } = useTranslation();
  const [currentQuestion, setCurrentQuestion] = useState<HostQuestion | null>(
    roomState.currentQuestion || null
  );
  const [timeRemaining, setTimeRemaining] = useState(roomState.currentQuestion?.timeLimit ?? 0);
  const [submissions, setSubmissions] = useState<Record<'A' | 'B', TeamSubmission | null>>({
    A: null,
    B: null,
  });
  const [gameEnded, setGameEnded] = useState(false);
  const [finalLeaderboard, setFinalLeaderboard] = useState<Array<{ playerId: string; playerName: string; score: number }>>([]);
  const [gameStatistics, setGameStatistics] = useState<GameStatistics | null>(null);
  const previousQuestionIdRef = useRef<string | undefined>(undefined);
  const gameEndData = useGameEndScreenData(roomState);

  useEffect(() => {
    if (roomState.gameState === 'finished' && gameEndData) {
      setGameEnded(true);
      setCurrentQuestion(null);
      setFinalLeaderboard(gameEndData.finalLeaderboard);
      setGameStatistics(gameEndData.gameStatistics);
    } else if (roomState.gameState === 'playing' && gameEnded) {
      setGameEnded(false);
      setCurrentQuestion(null);
      setFinalLeaderboard([]);
      setGameStatistics(null);
      setSubmissions({ A: null, B: null });
      previousQuestionIdRef.current = undefined;
    }
  }, [roomState.gameState, gameEndData, gameEnded]);

  useEffect(() => {
    const handleQuestion = (data: HostQuestion & { questionId?: string }) => {
      const qId = data.questionId || data.id;
      if (qId && qId === previousQuestionIdRef.current) return;
      previousQuestionIdRef.current = qId;
      setSubmissions({ A: null, B: null });
      setCurrentQuestion({ ...data, questionId: qId });
      setTimeRemaining(data.timeLimit);
    };

    const handleGameEnded = (data: { leaderboard?: typeof finalLeaderboard; statistics?: GameStatistics }) => {
      setGameEnded(true);
      setCurrentQuestion(null);
      setFinalLeaderboard(data.leaderboard || []);
      setGameStatistics(data.statistics || null);
    };

    const handleGameStarted = () => {
      setGameEnded(false);
      setCurrentQuestion(null);
      setFinalLeaderboard([]);
      setGameStatistics(null);
      setSubmissions({ A: null, B: null });
      previousQuestionIdRef.current = undefined;
    };

    socket.on('question', handleQuestion);
    socket.on('game-ended', handleGameEnded);
    socket.on('game-started', handleGameStarted);

    return () => {
      socket.off('question', handleQuestion);
      socket.off('game-ended', handleGameEnded);
      socket.off('game-started', handleGameStarted);
    };
  }, [socket]);

  useEffect(() => {
    const q = roomState.currentQuestion;
    if (!q) return;

    const questionId = q.questionId || q.id;
    if (questionId && questionId !== previousQuestionIdRef.current) {
      previousQuestionIdRef.current = questionId;
      setSubmissions({ A: null, B: null });
    }

    setCurrentQuestion(q);
    setTimeRemaining(
      calculateTimeRemaining(
        q as Parameters<typeof calculateTimeRemaining>[0],
        roomState.questionStartTime || q.startTime
      )
    );
  }, [roomState.currentQuestion, roomState.questionStartTime]);

  useEffect(() => {
    if (!currentQuestion) return;
    const interval = setInterval(() => {
      const start = roomState.questionStartTime || currentQuestion.startTime;
      const remaining = Math.max(0, currentQuestion.timeLimit - (Date.now() - start));
      setTimeRemaining(remaining);
    }, 200);
    return () => clearInterval(interval);
  }, [currentQuestion, roomState.questionStartTime]);

  useEffect(() => {
    const handleSubmission = (data: {
      questionId: string;
      teamId: 'A' | 'B';
      playerName: string;
      photoData: string;
      task?: HouseRunTask;
    }) => {
      const qId = currentQuestion?.questionId || currentQuestion?.id;
      if (data.questionId !== qId) return;
      setSubmissions((prev) => ({
        ...prev,
        [data.teamId]: {
          teamId: data.teamId,
          playerName: data.playerName,
          photoData: data.photoData,
          task: data.task,
        },
      }));
    };

    const handleResolved = (data: {
      questionId: string;
      teamId: 'A' | 'B';
      accepted: boolean;
    }) => {
      const qId = currentQuestion?.questionId || currentQuestion?.id;
      if (data.questionId !== qId) return;
      setSubmissions((prev) => {
        const sub = prev[data.teamId];
        if (!sub) return prev;
        return {
          ...prev,
          [data.teamId]: { ...sub, resolved: true, accepted: data.accepted },
        };
      });
    };

    socket.on('house-run-host-submission', handleSubmission);
    socket.on('house-run-team-resolved', handleResolved);

    return () => {
      socket.off('house-run-host-submission', handleSubmission);
      socket.off('house-run-team-resolved', handleResolved);
    };
  }, [socket, currentQuestion]);

  if (gameEnded) {
    return (
      <GameEndScreen
        finalLeaderboard={finalLeaderboard}
        gameStatistics={gameStatistics}
        onPlaySameGame={() => {
          socket.emit('start-game', { roomName: roomState.roomName, gameType: roomState.gameType });
          setGameEnded(false);
        }}
        onPlayAnotherGame={onBackToGameSelection || (() => {})}
        onBackToGameSelection={onBackToGameSelection}
        roomName={roomState.roomName}
        gameType={roomState.gameType}
      />
    );
  }

  if (!currentQuestion && roomState.gameState === 'playing') {
    return (
      <LoadingQuestion
        socket={socket}
        roomName={roomState.roomName}
        title={t('gameplay.loading.question')}
        onGoHome={onGoHome}
        onLongWaitTimeout={onLoadingTimeout}
      />
    );
  }

  if (!currentQuestion) {
    return (
      <div className="app" style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E1E2E 100%)' }}>
        <Header roomCode={roomState.roomName} />
        <WaitingSpinner message={t('gameplay.houseRun.waiting')} isLoading />
      </div>
    );
  }

  const themeTitle = currentQuestion.title || t('gameplay.houseRun.hostTitle');
  const timeSeconds = Math.ceil(timeRemaining / 1000);
  const progress = currentQuestion.timeLimit > 0 ? timeRemaining / currentQuestion.timeLimit : 0;

  const renderTeamPanel = (teamId: 'A' | 'B') => {
    const sub = submissions[teamId];
    const pendingTask = teamId === 'A' ? currentQuestion.taskA : currentQuestion.taskB;
    return (
      <div
        key={teamId}
        style={{
          flex: 1,
          minWidth: 0,
          background: 'rgba(255,255,255,0.05)',
          borderRadius: '1rem',
          padding: '1rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75rem',
        }}
      >
        <h3 style={{ margin: 0, textAlign: 'center', fontSize: '1.1rem' }}>
          {t('gameplay.houseRun.teamPanel', { team: teamId })}
        </h3>

        {!sub && pendingTask && <HouseRunTaskCard task={pendingTask} teamId={teamId} />}

        {!sub && !pendingTask && (
          <p style={{ textAlign: 'center', opacity: 0.6, margin: 'auto 0', fontSize: '0.9rem' }}>
            {t('gameplay.houseRun.hunting')}
          </p>
        )}

        {sub && (
          <>
            <p style={{ textAlign: 'center', margin: 0, fontSize: '0.85rem', opacity: 0.8 }}>
              {sub.playerName}
            </p>
            {sub.task && <HouseRunTaskCard task={sub.task} />}
            <img
              src={sub.photoData}
              alt=""
              style={{
                width: '100%',
                maxHeight: '220px',
                objectFit: 'cover',
                borderRadius: '0.5rem',
              }}
            />
            {sub.resolved && (
              <p
                style={{
                  textAlign: 'center',
                  margin: 0,
                  fontWeight: 700,
                  color: sub.accepted ? '#22C55E' : '#F87171',
                }}
              >
                {sub.accepted ? t('gameplay.houseRun.accepted') : t('gameplay.houseRun.rejected')}
              </p>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div className="app" style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E1E2E 100%)' }}>
      <Header
        leftButton={
          onBackToGameSelection
            ? { icon: HeaderIcons.back, onClick: onBackToGameSelection, tooltip: t('common.back') }
            : undefined
        }
        rightButton={{ icon: HeaderIcons.refresh, onClick: () => window.location.reload(), tooltip: t('common.refresh') }}
        roomCode={roomState.roomName}
      />

      <div className="game-host-layout" style={{ display: 'flex', gap: '1rem', padding: '1rem', height: 'calc(100vh - 80px)' }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '1rem', minWidth: 0 }}>
          <TimerSection
            timeSeconds={timeSeconds}
            progress={progress}
            questionNumber={currentQuestion.questionNumber}
            totalQuestions={currentQuestion.totalQuestions}
            primaryColor="#8B5CF6"
          />

          <h2 style={{ textAlign: 'center', margin: 0, fontSize: '1.5rem' }}>{themeTitle}</h2>

          <p style={{ textAlign: 'center', margin: 0, opacity: 0.75, fontSize: '0.95rem' }}>
            {t('gameplay.houseRun.hostHint')}
          </p>

          <div style={{ display: 'flex', gap: '1rem', flex: 1, minHeight: 0 }}>
            {renderTeamPanel('A')}
            {renderTeamPanel('B')}
          </div>
        </div>

        <StandingsSidebar
          players={roomState.players}
          liveAnswers={[]}
          totalPlayers={roomState.players.length}
          questionId={currentQuestion.questionId || currentQuestion.id}
          mode={gameUsesTeams(roomState.gameType) ? 'teams' : 'players'}
          teams={roomState.teams}
          shouldUpdateScores
        />
      </div>
    </div>
  );
};

export default HouseRunHostDisplay;
